"use client";
import {JSX, ReactNode} from "react";

import classNames from "classnames";
import {useSnapshot} from "valtio";

import {type SpaceProps, useSectionSpace} from "@/sections/use-section-space";

import {useModel} from "@/shared/hooks/state-management/use-model";
import {useTranslation} from "@/shared/i18n/use-translation";
import {FieldInstance} from "@/shared/models/form/fields";
import {InfoCardProps} from "@/shared/ui/data-display/cards";
import {Tag} from "@/shared/ui/data-display/tags/tag-base";
import {ContainerBase} from "@/shared/ui/layout/container-base";

import {PopularPostsContent} from "./parts/popular-posts-content";
import {PopularPostsFilters} from "./parts/popular-posts-filters";
import {PopularPostsHeader} from "./parts/popular-posts-header";
import {PopularPostsLoadMore} from "./parts/popular-posts-load-more";
import {PopularPostsModel} from "./popular-posts.model";

import "@/sections/blog/popular-posts/styles/popular-posts.scss";

export interface PopularPostsData {
    title?: string;
    tags: ReadonlyArray<Tag>;
    posts: ReadonlyArray<InfoCardProps>;
    hasMore?: boolean;
}

export interface PopularPostsBaseProps extends PopularPostsData {
    space?: SpaceProps;
    showTagSlider?: boolean;
    showSearch?: boolean;
    showMoreText?: string;
    backToBlogsText?: string;
    pageSize?: number;
    initialTag?: string;
    children?: ReactNode;
    anchorId?: string;
}

export interface PopularPostsProps extends PopularPostsBaseProps {
    visiblePosts: ReadonlyArray<InfoCardProps>;
    hasMore: boolean;
    activeTag?: string;
    searchModel?: FieldInstance;
    onTagChange?: (tag: string) => void;
    onLoadMore?: () => void;
    isLoading?: boolean;
    isButtonLoading?: boolean;
    noResultsText?: string;
    className?: string;
}

/**
 * Dumb UI component - receives everything via props
 */
export function PopularPosts({
    title,
    tags,
    visiblePosts,
    hasMore,
    activeTag,
    searchModel,
    onTagChange,
    onLoadMore,
    space,
    showTagSlider = true,
    showSearch = true,
    showMoreText,
    children,
    isLoading = false,
    isButtonLoading = false,
    noResultsText,
    className,
    anchorId,
}: PopularPostsProps): JSX.Element | null {
    const {t} = useTranslation();
    const sectionVars = useSectionSpace(space);

    const hasTags = !!tags && tags.length > 0;

    const displayTitle = title ?? t("blog.popularPosts");
    const displayShowMoreText = showMoreText ?? t("common.learnMore");
    const displayNoResultsText = noResultsText ?? t("search.noResultsFound");

    return (
        <section className={classNames("popular-posts", className)} style={sectionVars} id={anchorId}>
            <ContainerBase>
                <PopularPostsHeader title={displayTitle} />

                <PopularPostsFilters
                    showTagSlider={showTagSlider}
                    hasTags={hasTags}
                    tags={tags}
                    activeTag={activeTag}
                    onTagChange={onTagChange}
                    showSearch={showSearch}
                    searchModel={searchModel}
                />

                <PopularPostsContent isLoading={isLoading} visiblePosts={visiblePosts} noResultsText={displayNoResultsText} />

                <PopularPostsLoadMore
                    hasMore={hasMore}
                    onLoadMore={onLoadMore}
                    isButtonLoading={isButtonLoading}
                    showMoreText={displayShowMoreText}
                />

                {children && <div className="popular-posts__button-wrap">{children}</div>}
            </ContainerBase>
        </section>
    );
}

export type PopularPostsViewProps = PopularPostsBaseProps;

/**
 * Smart wrapper component - creates model internally
 */
export function PopularPostsView(props: PopularPostsViewProps): JSX.Element {
    const [model] = useModel(PopularPostsModel, {
        tags: props.tags,
        posts: props.posts,
        pageSize: props.pageSize ?? 8,
        initialTag: props.initialTag ?? "All",
    });
    const snapshot = useSnapshot(model);

    return (
        <PopularPosts
            {...props}
            visiblePosts={snapshot.visiblePosts as Array<InfoCardProps>}
            hasMore={snapshot.hasMore}
            activeTag={snapshot.activeTag}
            searchModel={model.search}
            onTagChange={(tag) => model.setActiveTag(tag)}
            onLoadMore={() => model.loadMore()}
        />
    );
}
